import type { Dictionary } from './es-MX';

/**
 * en-US — the MIRROR of es-MX (§1.2). Translate meaning, not words. Same register:
 * direct, craft-technical. Must satisfy the Dictionary shape derived from es-MX.
 */
export const enUS: Dictionary = {
  locale: 'en-US',
  localeName: 'English',
  otherLocaleName: 'Español',

  nav: {
    bench: 'Bench',
    studio: 'Studio',
    vocal: 'Vocals',
    packs: 'Packs',
    submissions: 'Submissions',
    market: 'Market',
    directory: 'Directory',
    purchases: 'Purchases',
    account: 'Account',
    upload: 'Upload',
  },

  common: {
    brand: 'DJMEXXICO',
    tagline: 'Exclusive cuts',
    loading: 'Loading…',
    loadingMore: 'Loading more…',
    retry: 'Retry',
    cancel: 'Cancel',
    close: 'Close',
    back: 'Back',
    save: 'Save',
    saving: 'Saving…',
    confirm: 'Confirm',
    delete: 'Delete',
    signOut: 'Sign out',
    of: 'of',
    free: 'Free',
    required: 'required',
  },

  errors: {
    generic: 'Something broke. Try again.',
    network: 'No connection. Check your network.',
    rateLimited: (secs: number) => `Too many requests. Retry in ${secs}s.`,
    unauthorized: 'Your session expired. Sign in again.',
    notFound: "We couldn't find that.",
    tooLarge: 'The file exceeds your plan limit.',
    unsupported: 'Unsupported format. Use WAV, AIFF or MP3.',
    fieldRequired: 'This field is required.',
    badEmail: 'Invalid email.',
    shortPassword: 'At least 8 characters.',
  },

  states: {
    pending: 'QUEUED',
    processing: 'PROCESSING',
    review: 'REVIEW CUTS',
    complete: 'READY',
    failed: 'FAILED',
    sold: 'SOLD',
    draft: 'DRAFT',
    published: 'PUBLISHED',
  },

  landing: {
    heroKicker: 'PRODUCERS · VOCALISTS · DJS',
    heroTitle: 'Upload your beat.\nCut the exclusive.',
    heroBody:
      'DJMEXXICO cuts your sound into exclusives. Upload a raw beat, the machine finds the cuts, you review them, and in minutes you have a sample pack ready to sell.',
    ctaPrimary: 'Create account',
    ctaSecondary: 'I have an account',
    step1Title: 'Upload',
    step1Body: 'Drag in your WAV, AIFF or MP3. Up to 200 MB. The cut starts on its own.',
    step2Title: 'Review the cuts',
    step2Body: 'Cuts are auto-detected. Nudge them to the millisecond and confirm.',
    step3Title: 'Publish and get paid',
    step3Body: 'Build your pack, price it in USD, publish to the market. Payment lands direct.',
    machineShowsWork: 'The machine shows its work: states, timings, sizes. No spinner and a prayer.',
    footerRights: 'Exclusive cuts for the underground.',
  },

  auth: {
    signInTitle: 'Enter the cutting room',
    signUpTitle: 'Open your cutting room',
    signInSubtitle: 'Your session, your cuts, your catalog.',
    signUpSubtitle: 'Create your account and upload your first beat.',
    nameLabel: 'Name',
    namePlaceholder: 'Your producer name',
    emailLabel: 'Email',
    passwordLabel: 'Password',
    passwordHint: 'At least 8 characters.',
    signInCta: 'Sign in',
    signUpCta: 'Create account',
    toSignUp: "No account? Create one",
    toSignIn: 'Already have an account? Sign in',
    working: 'Verifying…',
  },

  upload: {
    title: 'Upload studio',
    subtitle: 'Upload a beat and the machine finds the cuts.',
    dropTitle: 'Drop your beat here',
    dropHint: 'or tap to browse · WAV, AIFF, MP3 · max 200 MB',
    browse: 'Browse file',
    uploading: 'Uploading',
    processing: 'Registering the cut…',
    cancel: 'Cancel upload',
    successTitle: 'Beat on the line',
    successBody: 'Your beat is in. We’ll tell you when the cuts are ready to review.',
    goToReview: 'Go review cuts',
    another: 'Upload another',
    ofSize: (done: string, total: string) => `${done} of ${total}`,
  },

  review: {
    title: 'Review the cuts',
    subtitle: 'Nudge and confirm before the pack renders.',
    waitingTitle: 'Finding the cuts',
    waitingBody: 'This takes a moment. The machine analyzes your beat and marks the cut points.',
    countLabel: (total: number, confirmed: number) => `${total} cuts · ${confirmed} confirmed`,
    confirmCta: (confirmed: number, total: number) => `Confirm cuts (${confirmed}/${total})`,
    rendering: 'Rendering pack…',
    renderedTitle: 'Pack rendered',
    renderedBody: 'Your cuts are locked. The pack is ready in your library.',
    goToLibrary: 'See in library',
    marker: {
      detected: 'detected',
      adjusted: 'adjusted',
      confirmed: 'confirmed',
    },
    toolbar: {
      nudgeBack: '−10 ms',
      nudgeFwd: '+10 ms',
      playSeg: 'Play segment',
      confirmOne: 'Confirm cut',
      deleteOne: 'Delete cut',
      addMarker: 'Add cut',
    },
    ariaMarker: (n: number, time: string, state: string) => `Cut ${n} — ${time}, ${state}`,
    announceConfirmed: (n: number) => `Cut ${n} confirmed`,
    announceAdjusted: (n: number, time: string) => `Cut ${n} moved to ${time}`,
    keysTitle: 'Keyboard shortcuts',
    keys: [
      ['Tab', 'Cycle cuts'],
      ['← / →', 'Nudge 10 ms'],
      ['Shift + ← / →', 'Nudge 1 ms (fine)'],
      ['Ctrl + ← / →', 'Nudge 100 ms (coarse)'],
      ['Enter', 'Confirm cut'],
      ['Del', 'Delete cut'],
      ['Space', 'Play / pause segment'],
      ['A', 'Add cut at playhead'],
      ['Esc', 'Deselect'],
    ] as [string, string][],
    failedTitle: 'The cut failed on this beat',
    failedBody: 'Something went wrong processing it. You can retry the upload.',
  },


  bench: {
    title: 'Your bench',
    subtitle: 'Where your sound stands, and what you do next.',
    yourMove: 'Your move',
    dig: 'Dig',
    digVerb: 'Bring it in',
    chop: 'Chop',
    chopVerb: 'Cut it',
    pack: 'Pack',
    packVerb: 'On the bench',
    ship: 'Ship',
    shipVerb: 'Sell it',
    move: {
      dig: {
        title: 'Bring in a beat.',
        body: 'Nothing on the bench yet. Drop in a raw beat and the cut starts.',
        cta: 'Bring in a beat',
      },
      chop: {
        title: 'Cut your beat.',
        body: (title: string) => `${title} is sliced and waiting on your call. Nudge the cuts, lock them in.`,
        cta: 'Cut it',
      },
      ship: {
        title: 'Ship your pack.',
        body: (title: string) => `${title} is packed and ready. Price it, publish it, get paid direct.`,
        cta: 'Ship it',
      },
      wait: {
        title: 'The bench is working.',
        body: (n: number) => `${n} ${n === 1 ? 'beat is' : 'beats are'} on the line. Cuts, render, pack.`,
        cta: 'Nothing for you to do yet — the bench shows its work.',
      },
    },
    failed: (n: number) => `${n} ${n === 1 ? 'beat' : 'beats'} the bench could not finish.`,
    failedCta: 'Open library',
    unresumable: (n: number) =>
      `${n} older ${n === 1 ? 'cut is' : 'cuts are'} waiting but cannot be reopened from here. Find them in your library.`,
  },
  library: {
    title: 'Your library',
    subtitle: 'Every cut you’ve made.',
    emptyTitle: 'Nothing cut yet',
    emptyBody: 'Upload your first beat and the cut starts.',
    emptyCta: 'Upload beat',
    downloadPack: 'Download pack',
    downloadPreview: 'Play preview',
    createListing: 'Publish to market',
    reviewCuts: 'Review cuts',
    showProvenance: 'View provenance',
    hideProvenance: 'Hide provenance',
    meta: (dur: string, bpm: string, fmt: string) => `${dur} · ${bpm} · ${fmt}`,
  },

  listing: {
    title: 'Publish your pack',
    subtitle: 'Build the listing and set the price.',
    fromAsset: 'From your asset',
    titleLabel: 'Pack title',
    titlePlaceholder: 'e.g. Dark Trap 140',
    descLabel: 'Description',
    descPlaceholder: 'Tell them what the pack brings, how it sounds, who it’s for.',
    priceLabel: 'Price',
    licenseLabel: 'License',
    license: {
      royalty_free: 'Royalty-free',
      exclusive: 'Exclusive',
      sync: 'Sync',
      wellness: 'Wellness',
    },
    memberOnly: 'Members only',
    publishCta: 'Publish pack',
    draftCta: 'Save draft',
    publishing: 'Publishing…',
    publishedTitle: 'Pack published',
    publishedBody: 'Your pack is on the market. We’ll ping you on every sale.',
    viewInMarket: 'See on the market',
    draftSavedTitle: 'Draft saved',
    draftSavedBody: 'Nothing is on the market yet. Publish when the pack is ready.',
  },

  market: {
    title: 'Market',
    subtitle: 'Community packs, ready for your next session.',
    searchPlaceholder: 'Search by name, genre, vibe…',
    resultCount: (n: number) => `${n} results`,
    filterAll: 'All',
    sortRecent: 'Recent',
    sortRelevance: 'Relevance',
    filterKind: 'Type',
    filterLicense: 'License',
    filterBpm: 'BPM',
    filterKey: 'Key',
    keyAny: 'Any',
    emptyTitle: 'Nothing here… yet',
    emptyBody: 'No pack matches your search. Try other filters.',
    buy: 'Buy',
    preview: 'Preview',
    pause: 'Pause',
    previewUnavailable: 'No preview yet',
    detailMeta: (samples: string, fmt: string, bpm: string) => `${samples} · ${fmt} · ${bpm}`,
    licenseLine: (lic: string) => `License: ${lic}`,
    byCreator: 'By producer',
    checkoutOpening: 'Opening secure checkout…',
    checkoutSuccess: 'Purchase confirmed. Check your email for the download.',
    checkoutCancel: 'Payment canceled.',
  },

  creator: {
    subtitle: 'Producer storefront',
    packCount: (n: number) => `${n} packs`,
    cosigns: (n: number) => `${n} co-${n === 1 ? 'sign' : 'signs'}`,
    notFoundTitle: 'Producer not found',
    notFoundBody: "This storefront doesn't exist or has no published packs yet.",
    emptyTitle: 'No packs yet',
    emptyBody: 'This producer has not published any packs yet.',
  },

  account: {
    title: 'Account',
    language: 'Language',
    session: 'Active session',
    signedInAs: (email: string) => `Signed in as ${email}`,
  },

  payouts: {
    title: 'Get paid',
    checking: 'Checking your payout status…',
    notStarted: 'You have not set up payouts yet. Connect your account and sales land direct.',
    inProgress: 'Setup in progress. A few steps are still pending.',
    restricted: 'Your payout account needs attention. Complete the pending details.',
    active: 'Payouts active. Every sale deposits direct to your account.',
    setupCta: 'Set up payouts',
    resumeCta: 'Resume setup',
    dashboardCta: 'Open payouts dashboard',
    opening: 'Opening…',
    stateLabel: 'Status',
    stateNotStarted: 'NOT SET UP',
    stateInProgress: 'IN PROGRESS',
    stateRestricted: 'NEEDS ATTENTION',
    stateActive: 'ACTIVE',
    composerNotice: 'To get paid for what you sell, set up payouts first. You can keep saving drafts meanwhile.',
    composerCta: 'Set up payouts first',
  },

  credits: {
    title: 'Credits',
    balanceLabel: 'Balance',
    balance: (n: number) => `${n} ${n === 1 ? 'credit' : 'credits'}`,
    packsLabel: 'Top-ups',
    packCta: (credits: number, price: string) => `${credits} credits · ${price}`,
    opening: 'Opening secure checkout…',
    success: 'Payment confirmed. Your credits are in your balance.',
    cancel: 'Payment canceled.',
  },

  purchases: {
    title: 'Purchases',
    subtitle: 'Your licenses and their downloads.',
    emptyTitle: 'No purchases yet',
    emptyBody: 'When you buy a pack on the market, its license and download show up here.',
    emptyCta: 'Go to the market',
    download: 'Download',
    viewCertificate: 'View certificate',
    downloadsLeft: (n: number) => `${n} ${n === 1 ? 'download left' : 'downloads left'}`,
    unlimited: 'Unlimited downloads',
    licenseLine: (lic: string) => `License: ${lic}`,
    stateRevoked: 'REVOKED',
    stateExpired: 'EXPIRED',
    stateExhausted: 'NO DOWNLOADS LEFT',
  },

  bloodline: {
    minted: 'BLOODLINE MINTED',
    source: 'SOURCE',
    thisPack: 'THIS PACK',
    derivedBody: (hops: number) =>
      `Every buyer receives this chain of custody with the pack — ${hops} ${hops === 1 ? 'hop' : 'hops'} back to the source. It travels with the sound.`,
    sourceBody:
      'This pack is the source. Everything cut from it carries this node, and every buyer receives the chain with the sound.',
  },

  cosign: {
    count: (n: number) => `${n} ${n === 1 ? 'producer has' : 'producers have'} co-signed this pack`,
    none: 'No co-signs yet',
    action: 'Co-sign this pack',
    done: 'You co-signed this',
    ownPack: 'Your pack — others co-sign it, you cannot.',
    explain: 'Stake your name. Your co-sign carries the standing you have earned.',
    signInPrompt: 'Sign in to co-sign',
  },

  provenance: {
    title: 'Chain of custody',
    loading: 'Resolving provenance…',
    originalRoot: 'Original with no derivations — it is the root of its own chain of custody.',
    thisAsset: 'THIS ASSET',
    hops: (n: number) => `${n} ${n === 1 ? 'hop' : 'hops'}`,
  },

  vocal: {
    title: 'Vocal studio',
    subtitle: 'Process your takes: range, tuning, and an acapella ready to sell.',
    profileTitle: 'Your vocal range',
    profileEmpty: 'No takes processed yet. Process one to build your vocal profile.',
    rangeLabel: 'Range',
    meanLabel: 'Mean pitch',
    keyLabel: 'Suggested key',
    takesLabel: (n: number) => `${n} ${n === 1 ? 'take processed' : 'takes processed'}`,
    sourcesTitle: 'Takes ready to process',
    sourcesEmpty: 'No audio ready. Upload a take in the studio.',
    processCta: 'Process vocals · 3 credits',
    processingCta: 'Processing…',
    jobQueued: 'Take on the line. The machine is separating and tuning your voice.',
    jobRunning: 'Processing your voice: range, breaths, and level.',
    jobDone: 'Acapella ready. We added it to your packs.',
    jobFailed: 'Processing failed. Try again.',
    packsTitle: 'Your acapellas',
    packsEmpty: 'Once you process a take, your acapella shows up here.',
    publishCta: 'Publish',
    submitCta: 'Send to producer',
    goUpload: 'Go to the studio',
    hz: (n: string) => `${n} Hz`,
  },

  submissions: {
    title: 'Submissions',
    subtitle: 'Send your acapellas to producers and respond to the ones you receive.',
    inbox: 'Received',
    outbox: 'Sent',
    emptyInbox: 'No submissions yet. When someone sends you an acapella, it shows up here.',
    emptyOutbox: 'You have not sent any submissions. Send an acapella from the vocal studio.',
    accept: 'Accept',
    decline: 'Decline',
    withdraw: 'Withdraw',
    fromLabel: 'From',
    toLabel: 'To',
    statusPending: 'PENDING',
    statusAccepted: 'ACCEPTED',
    statusDeclined: 'DECLINED',
    statusWithdrawn: 'WITHDRAWN',
    composeTitle: 'Send submission',
    composeBody: 'Share this acapella with a producer. You need their user identifier.',
    recipientLabel: 'Producer identifier',
    recipientHelp: 'Paste the user identifier of the producer who will receive the submission.',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Tell them what the take is about…',
    send: 'Send submission',
    sending: 'Sending…',
    sent: 'Submission sent.',
    accepted: 'Submission accepted.',
    declined: 'Submission declined.',
    withdrawn: 'Submission withdrawn.',
  },

  directory: {
    title: 'Directory',
    subtitle: 'Browse every creator with a published catalog.',
    sortRecent: 'Recent',
    sortActive: 'Most active',
    sortAlphabetical: 'A–Z',
    resultCount: (n: number) => `${n} creators`,
    packsCount: (n: number) => (n === 1 ? '1 pack' : `${n} packs`),
    viewStorefront: 'View storefront',
    emptyTitle: 'No creators yet',
    emptyBody: 'When someone publishes a pack, they show up here.',
  },

  onboarding: {
    welcomeTitle: 'Welcome to the cutting room',
    welcomeBody: (credits: number) =>
      `Your account starts with ${credits} credits for your first pack. Let's set up your profile.`,
    stepOf: (current: number, total: number) => `Step ${current} of ${total}`,
    handleStepTitle: 'Choose your handle',
    handleStepBody: 'This is how people find you in the directory and on your storefront.',
    handleLabel: 'Handle',
    handlePlaceholder: 'your-name',
    handleHelp: '3–32 chars: lowercase letters, numbers, and interior hyphens.',
    displayNameLabel: 'Public name',
    displayNamePlaceholder: 'Your artist name',
    uploadStepTitle: 'Upload your first pack',
    uploadStepBody: 'Take your sound to the studio and publish it to the market.',
    goToStudio: 'Go to studio',
    next: 'Next',
    back: 'Back',
    skip: 'Skip for now',
    finish: 'Done',
    saving: 'Saving…',
    saved: 'Profile saved.',
    handleTaken: 'That handle is already taken.',
    handleInvalid: '3–32 chars: lowercase letters, numbers, and interior hyphens.',
  },
};
